import { debounce } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { FormAutocomplete, FormSwitch, FormTextField } from 'src/components';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PartnerGroupRules, ProjectStages, usePartnerGroupRules } from 'src/graphql';
import { mapPlatformUsersToSortedOptions } from 'src/graphql/entities/platform-user/platform-user.mapping';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalState } from 'src/store/modals';
import { sortAlphabetically } from 'src/utils/sorting';

export enum AddProjectFormField {
  Address = 'address',
  Name = 'name',
  ProjectStage = 'projectStage',
  ProjectManager = 'projectManager',
  SigningDate = 'signingDate',
  EstimatedDay1 = 'estimatedDay1',
  WarrantyExpirationDate = 'warrantyExpirationDate',
  Day1Date = 'day1Date',
  TosAccepted = 'tosAccepted',
  WillReceiveEmails = 'willReceiveEmails',
  HasPreSoldMembership = 'hasPreSoldMembership',
}

export interface AddProjectFormValues {
  [AddProjectFormField.Address]: {
    id: string;
    label: string;
    hasPreSoldMembership: boolean;
    partnerId?: number;
  } | null;
  [AddProjectFormField.Name]: string;
  [AddProjectFormField.ProjectStage]: { id: number; label: string } | null;
  [AddProjectFormField.ProjectManager]: { id: string; label: string } | null;
  [AddProjectFormField.SigningDate]: Dayjs | null;
  [AddProjectFormField.EstimatedDay1]: Dayjs | null;
  [AddProjectFormField.WarrantyExpirationDate]: Dayjs | null;
  [AddProjectFormField.Day1Date]: Dayjs | null;
  [AddProjectFormField.TosAccepted]: boolean;
  [AddProjectFormField.WillReceiveEmails]: boolean;
  [AddProjectFormField.HasPreSoldMembership]: boolean;
}

export function useAddProjectForm({
  projectStages,
  formMethods,
  addNewSite,
}: {
  projectStages: { id: number; name: string }[];
  formMethods: ReturnType<typeof useForm<AddProjectFormValues>>;
  addNewSite: () => void;
}) {
  const { watch } = formMethods;

  const projectStage = watch(AddProjectFormField.ProjectStage);
  const selectedAddress = watch(AddProjectFormField.Address);

  const { modalProps } = useModalState(Modals.AddProject);

  const [searchUsersTerm, setSearchUsersTerm] = useState('');

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const partnerIdForSearch =
    modalProps?.address?.partnerId || selectedAddress?.partnerId || selectedPartnerId;

  const {
    records: activeUserRecords,
    isLoading: usersLoading,
    loadMore: usersLoadMore,
    hasMore: usersHasMore,
  } = useSearch({
    entityName: EntityNames.PlatformUser,
    searchTerm: searchUsersTerm,
    filter: [
      {
        attribute: 'isActive',
        operator: '=',
        value: 'true',
      },
    ],
    partnerId: partnerIdForSearch,
    shouldIncludePlatformPartnerId: false,
    showResultsWhenEmptySearchTerm: true,
  }) ?? {};

  const userOptions = useMemo(
    () => mapPlatformUsersToSortedOptions(activeUserRecords) ?? [],
    [activeUserRecords],
  );

  const { partnerGroupRules } = usePartnerGroupRules(partnerIdForSearch);

  const hasAccessToSubscriptionManagement =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement];

  const hasAccessToPreSoldMemberships =
    partnerGroupRules[PartnerGroupRules.AccessToPresoldMemberships];

  const hasAccessToSubscriptionManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];

  const [searchAddressTerm, setSearchAddressTerm] = useState('');

  const addressFilters = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (!isNullish(partnerIdForSearch)) {
      filters.push({
        attribute: 'partnerId',
        operator: '=',
        value: String(partnerIdForSearch),
      });
    }

    return filters;
  }, [partnerIdForSearch]);

  const {
    records: addresses,
    isLoading: addressLoading,
    loadMore: addressLoadMore,
    hasMore: addressHasMore,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm: searchAddressTerm,
    sort: { attribute: 'address1', desc: false },
    showResultsWhenEmptySearchTerm: true,
    partnerId: partnerIdForSearch,
    filter: addressFilters,
  }) ?? {};

  const searchAddress = useMemo(
    () => debounce((query: string) => setSearchAddressTerm(query), SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const addressOptions = useMemo(() => {
    if (!addresses) return [];
    return addresses
      ?.filter(
        (address, index, arr) =>
          arr.findIndex((a) => a.addressId === address.addressId) === index,
      )
      .map((address) => ({
        id: address.addressId,
        label: address.fullAddress ?? '',
        hasPreSoldMembership: !!address.preSoldMemberships?.length,
      }))
      .sort(sortAlphabetically('label'));
  }, [addresses]);

  const clearFields = useCallback(() => {
    setSearchAddressTerm('');
  }, []);

  const showPsmToggle =
    hasAccessToSubscriptionManagement &&
    hasAccessToPreSoldMemberships &&
    !selectedAddress?.hasPreSoldMembership;

  const addProjectFormFields = useMemo(() => {
    return (
      <>
        <FormAutocomplete
          type="autocomplete"
          label="Site"
          options={addressOptions}
          name={AddProjectFormField.Address}
          data-testid="add-project-site"
          required="Site is required"
          showErrorText
          creatable
          onAddNewItem={addNewSite}
          onAddNewItemLabel="Create Site"
          loading={addressLoading}
          hasMore={addressHasMore}
          onLoadMore={addressLoadMore}
          onSearchChange={searchAddress}
        />
        <FormTextField
          type="textfield"
          name={AddProjectFormField.Name}
          data-testid="add-project-name"
          label="Project Name"
          required="Project name is required"
          showErrorText
        />
        <FormAutocomplete
          type="autocomplete"
          name={AddProjectFormField.ProjectStage}
          data-testid="add-project-stage"
          label="Stage"
          options={projectStages.map((stage) => ({
            id: stage.id,
            label: stage.name,
          }))}
          clearable={false}
        />
        <FormAutocomplete
          type="autocomplete"
          name={AddProjectFormField.ProjectManager}
          data-testid="add-project-manager"
          label="Project Manager"
          options={userOptions}
          loading={usersLoading}
          hasMore={usersHasMore}
          onLoadMore={usersLoadMore}
          onSearchChange={setSearchUsersTerm}
        />
        {projectStage?.id === ProjectStages.Proposal && (
          <FormDatePicker
            type="datepicker"
            name={AddProjectFormField.SigningDate}
            value={null}
            label="Signing Date"
          />
        )}
        {projectStage?.id === ProjectStages.ActiveProject && (
          <FormDatePicker
            type="datepicker"
            name={AddProjectFormField.EstimatedDay1}
            label="Estimated Day1 Date"
          />
        )}
        {projectStage?.id === ProjectStages.ServiceClient && (
          <>
            <FormDatePicker
              type="datepicker"
              name={AddProjectFormField.Day1Date}
              label="Day1 Start date"
            />
            <FormDatePicker
              type="datepicker"
              name={AddProjectFormField.WarrantyExpirationDate}
              label="Warranty Expiration Date"
            />
          </>
        )}
        <FormSwitch
          type="switch"
          name={AddProjectFormField.TosAccepted}
          data-testid="add-project-tos-accepted"
          label="Terms of Service Accepted"
        />
        {!!hasAccessToSubscriptionManagementPlus && (
          <FormSwitch
            type="switch"
            name={AddProjectFormField.WillReceiveEmails}
            data-testid="add-project-educational-emails"
            label="Send Educational Emails"
          />
        )}
        {!!projectStage &&
          [ProjectStages.ActiveProject, ProjectStages.ServiceClient].includes(
            projectStage.id,
          ) &&
          !!showPsmToggle && (
            <FormSwitch
              type="switch"
              name={AddProjectFormField.HasPreSoldMembership}
              data-testid="add-project-pre-sold-membership"
              label="Did you pre-sell a membership?"
            />
          )}
      </>
    );
  }, [
    addressOptions,
    addNewSite,
    addressLoading,
    addressHasMore,
    addressLoadMore,
    searchAddress,
    projectStages,
    userOptions,
    usersLoading,
    usersHasMore,
    usersLoadMore,
    projectStage,
    hasAccessToSubscriptionManagementPlus,
    showPsmToggle,
  ]);

  return { addProjectFormFields, clearFields };
}
